import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  adminRoutes,
  heatingCalcRoutes,
  publicRoutes,
  limitedContentUserRoutes,
} from "./page-routes";
import { ROLES, ROLE_GROUPS, getDefaultRoute } from "../constants/roles";
import ProtectedRoute from "../components/shared/routes/protected-route/protected-route";
import PublicRoute from "../components/shared/routes/public-route/public-route";

const MainRoutes = () => {
  const { isAuth, adminRole } = useSelector((state) => state.auth);

  // Helper function to render nested routes
  const renderNestedRoutes = (routes, requiredRole = null) => {
    const flatRoutes = [];

    routes?.forEach((route) => {
      // Add the main route
      flatRoutes.push({
        path: route.path,
        element: (
          <ProtectedRoute requiredRole={requiredRole}>
            {route.element}
          </ProtectedRoute>
        ),
        key: route.path,
      });

      // Add child routes as separate flat routes
      if (route.children && route.children.length > 0) {
        route.children.forEach((childRoute) => {
          flatRoutes.push({
            path: childRoute.path,
            element: (
              <ProtectedRoute requiredRole={requiredRole}>
                {childRoute.element}
              </ProtectedRoute>
            ),
            key: childRoute.path,
          });
        });
      }
    });

    return flatRoutes.map((route) => (
      <Route key={route.key} path={route.path} element={route.element} />
    ));
  };

  return (
    <Routes>
      {/* Public routes - accessible only when NOT authenticated */}
      {publicRoutes?.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={<PublicRoute>{route.element}</PublicRoute>}
        />
      ))}

      {/* Root route - redirect to appropriate page based on role */}
      <Route
        path="/"
        element={
          isAuth && adminRole ? (
            <Navigate to={getDefaultRoute(adminRole)} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Admin routes (Super Admin role 5 and Admin role 4) - only render if user has admin role */}
      {isAuth &&
        adminRole &&
        ROLE_GROUPS.ADMIN_ROLES.includes(adminRole) &&
        renderNestedRoutes(adminRoutes, ROLE_GROUPS.ADMIN_ROLES)}

      {/* Heating User routes (role 3) - only render if user has heating user role */}
      {isAuth &&
        adminRole === ROLES.HEATING_USER &&
        renderNestedRoutes(heatingCalcRoutes, ROLES.HEATING_USER)}

      {/* Limited Content User routes (role 6) - only render if user has limited content user role */}
      {isAuth &&
        adminRole === ROLES.LIMITED_CONTENT_USER &&
        renderNestedRoutes(
          limitedContentUserRoutes,
          ROLES.LIMITED_CONTENT_USER,
        )}

      {/* Catch-all route - redirect based on authentication */}
      <Route
        path="*"
        element={
          isAuth && adminRole ? (
            <Navigate to={getDefaultRoute(adminRole)} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
};

export default MainRoutes;
